import {extendObservable, makeAutoObservable, runInAction, toJS} from 'mobx';
import {cloneDeep} from 'lodash'
import {EventType} from "./notificationTriggersStore";
import {
  checkValidName,
  createNotificationTrigger,
  testNotificationTrigger,
  updateNotificationTrigger
} from "./notificationTriggersApi";
import defaultSessionStore from "../../../services/session/SessionStore";

class CreateOrEditStore {
  mode;
  trigger;
  originalTrigger;
  errors;
  hasValidName = true;
  checkingValidName;

  constructor(trigger) {
    window.NotificationTriggerEditor = this;
    makeAutoObservable(this, {}, { autoBind: true })
    this.mode = trigger ? CreateOrEditMode.EDIT : CreateOrEditMode.CREATE;
    if(trigger) {
      this.trigger = trigger;
      // optional field that is not always available
      if(!trigger.criteria) {
        // needs to be an object first or extendObservable won't work
        this.trigger.criteria = {}
        extendObservable(this.trigger.criteria, this.criteriaByEventType(trigger.type));
      }
    } else {
      const clone = cloneDeep(emptyTrigger)
      this.trigger = {...clone}
    }
    this.originalTrigger = toJS(this.trigger)
  }

  setName(value) {
    this.trigger.name = value;
  };

  onNameBlur() {
    if(this.originalTrigger && this.trigger.name === this.originalTrigger.name) {
      this.hasValidName = true;
    } else{
      const trimmed = this.trigger.name.trim();
      if(trimmed) {
        this.checkingValidName = true;
        checkValidName(trimmed).then(resp => runInAction(() => {
          this.hasValidName = resp.isUnique;
          this.checkingValidName = false;
        }))
      }
    }
  }

  setDeliveryMethod(field, value) {
    switch (field) {
      case "notificationInbox":
        this.trigger.channels.notificationInbox = value;
        break;
      case "email":
        this.trigger.channels.email.enabled = value;
        break;
    }
  }

  setEventType(type) {
    this.trigger.eventType = type;
    const oldCriteria = toJS(this.trigger.criteria)
    this.trigger.criteria = this.criteriaByEventType(type, oldCriteria)
  }

  criteriaByEventType (type, oldCriteria) {
    let newCriteria;
    switch (type) {
      case EventType.WORKFLOW_TASK_APPROVAL_NEEDED:
        newCriteria = {
          type: EventType.WORKFLOW_TASK_APPROVAL_NEEDED,
          taskType: [],
          priority: []
        }
        break;
      case EventType.WORKFLOW_TASK_ASSIGNED:
        newCriteria = {
          type: EventType.WORKFLOW_TASK_ASSIGNED,
          priority: []
        }
        break;
      case EventType.WORKFLOW_TASK_OVERDUE:
        newCriteria = {
          type: EventType.WORKFLOW_TASK_OVERDUE,
          priority: []
        }
        break;
      case EventType.REVIEW_UPDATED:
        newCriteria = {
          type: EventType.REVIEW_UPDATED,
          reviewProvider: [],
          keywords: []
        }
        break;
      case EventType.GOOGLE_QUESTIONS_AND_ANSWERS:
        newCriteria = {
          type: EventType.GOOGLE_QUESTIONS_AND_ANSWERS,
          publisher: [],
          postType: [],
          keywords: []
        }
        break;
      case EventType.CREDENTIAL_EXPIRED:
        newCriteria = {
          type: EventType.CREDENTIAL_EXPIRED,
          thresholdDays: 0,
          secondThreshold: undefined
        }
        break;
      case EventType.REVIEW_RECEIVED:
      default:
        newCriteria = {
          type: EventType.REVIEW_RECEIVED,
          reviewStars: [],
          reviewProvider: [],
          keywords: []
        }
        break;
    }
    if(oldCriteria) {
      Object.keys(oldCriteria)
        .filter(key => key !== 'type').forEach(key => {
          if(newCriteria[key]) {
            newCriteria[key] = oldCriteria[key];
          }
      })
    }
    return newCriteria;
  }

  setReviewReceivedCriteria(key, value) {
    // something was getting mixed up when just setting the array values not causing a redraw, so copying them seemed to work
    switch (key) {
      case "reviewStars":
        this.trigger.criteria.reviewStars = [...value];
        break;
      case "reviewProvider":
        this.trigger.criteria.reviewProvider = [...value];
        break;
      case "keywords":
        this.trigger.criteria.keywords = value
        break;
    }
  }
  setWorkflowTaskApprovalCriteria(key, value) {
    switch (key) {
      case "taskType":
        this.trigger.criteria.taskType = [...value];
        break;
      case "priority":
        this.trigger.criteria.priority = [...value];
        break;
    }
  }

  setWorkflowTaskAssignedCriteria(key, value) {
    switch (key) {
      case "priority":
        this.trigger.criteria.priority = [...value];
        break;
    }
  }
  setGoogleQACriteria(key, value) {
    switch (key) {
      case "publisher":
        this.trigger.criteria.publisher = [...value];
        break;
      case "postType":
        this.trigger.criteria.postType = [...value];
        break;
      case "keywords":
        this.trigger.criteria.keywords = value
        break;
    }
  }

  setReviewUpdatedCriteria(key, value) {
    switch (key) {
      case "reviewProvider":
        this.trigger.criteria.reviewProvider = [...value];
        break;
      case "keywords":
        this.trigger.criteria.keywords = value
        break;
    }
  }

  setCredentialExpiration(value) {
    this.trigger.criteria.thresholdDays = value.thresholdDays
    this.trigger.criteria.secondThreshold = value.secondThreshold
  }

  setRecipientTypes(all) {
    this.trigger.recipients.all = all;
    if(all) {
      this.trigger.recipients.userIds = [];
      this.trigger.recipients.userRoles = [];
    }
  }

  setUsers(users) {
    this.trigger.recipients.userIds  = users
  }

  setUserRoles(roles) {
    this.trigger.recipients.userRoles = roles
  }

  get hasValidAssignees() {
    const { recipients } = this.trigger;
    return !!(recipients.all || recipients.userIds.length || recipients.userRoles.length);
  }
  // TODO - verify against data models what are options iterables and clear those not just all criteria
  get triggerForApi() {
    const triggerAsJs = toJS(this.trigger);
    triggerAsJs.name = triggerAsJs.name.trim();
    switch (triggerAsJs.eventType) {
      case EventType.REVIEW_RECEIVED:
        if(triggerAsJs.criteria.reviewStars && triggerAsJs.criteria.reviewStars.length){
          const recommendations = triggerAsJs.criteria.reviewStars.filter(star => isNaN(star))
          triggerAsJs.criteria.reviewStars = triggerAsJs.criteria.reviewStars.filter(star => !isNaN(star))
          triggerAsJs.criteria.reviewRecommendations = recommendations;
        }
        break
      case EventType.REVIEW_UPDATED:
        if(!triggerAsJs.criteria.reviewProvider.length) {
          triggerAsJs.criteria = undefined
        }
    }
    return triggerAsJs;
  }

  get isSavable() {
    const trimmed = this.trigger.name.trim()
    return this.hasValidName && this.hasValidAssignees && !!trimmed.length;
  }

  saveNotificationTrigger() {
    this.savingNotificationTrigger = true;
    const trigger = this.triggerForApi;
    trigger.accountId = defaultSessionStore.user.accountId;
    const promise = trigger._id ? updateNotificationTrigger : createNotificationTrigger;

    return promise(trigger).then(() => {
      runInAction(() => {
        this.savingNotificationTrigger = false;
        this.notificationTrigger = undefined;
      })
    })
  }

  testTrigger(delay = false) {
    if (!this.trigger._id) throw new Error("must save trigger before testing it")
    return testNotificationTrigger(this.trigger._id, { delay })
  }

}

export const emptyTrigger = {
  channels: {
    email: {
      enabled: true,
      sendImmediately: false
    },
    notificationInbox: false,
  },
  recipients: {
    all: true,
    userIds: [],
    userRoles: []
  },
  criteria: {
    //TODO - type is a bit over redundant but required
    type: 'ReviewReceived',
    reviewStars: [],
    reviewProvider: [],
    keywords: []
  },
  name: "",
  eventType: "ReviewReceived",
}

export const CreateOrEditMode  = {
  EDIT: "CreateOrEditStore.EDIT",
  CREATE: "CreateOrEditStore.CREATE"
}

export default CreateOrEditStore;
